import { z } from "zod";

// #region UserId

// TODO: Probably retain this for non-preview use.

const USER_SOURCE_CODES = {
  vision_auth: "2",
  legacy_app: "3",
} as const;

export type UserSource = keyof typeof USER_SOURCE_CODES;

const REVERSE_USER_SOURCE_CODES: Record<string, UserSource> = {
  "2": "vision_auth",
  "3": "legacy_app",
} as const;

const USER_MAX_UNIQUE_LENGTH = 128;
const USER_UNIQUE_PATTERN = /^[a-zA-Z0-9|_-]+$/;

declare const UserIdBrand: unique symbol;
declare const UserIdSourceBrand: unique symbol;

/**
 * A unique identifier for a User.
 *
 * Structure:
 * - Source: the source system ('vision_auth' or 'legacy_app')
 * - Unique: a URL-safe string (1-128 chars, [a-zA-Z0-9_-])
 */
export type UserId<S extends UserSource = UserSource> = string & {
  readonly [UserIdBrand]: "UserId";
  readonly [UserIdSourceBrand]: S;
};

export class UserIdError extends Error {
  constructor(
    public readonly kind: "OutOfRange" | "Format" | "InvalidSource",
    message: string,
  ) {
    super(message);
  }
}

/**
 * Constructs a UserId from its component parts or throws a UserIdError.
 *
 * @param source - The source system ('vision_auth' or 'legacy_app')
 * @param unique - URL-safe string (1-128 chars, [a-zA-Z0-9|_-])
 */
const userIdFromParts = <S extends UserSource = UserSource>(source: S, unique: string): UserId<S> => {
  if (!(source in USER_SOURCE_CODES)) {
    throw new UserIdError(
      "InvalidSource",
      `Source ${source} is not valid. Expected one of: ${Object.keys(USER_SOURCE_CODES).join(", ")}.`,
    );
  }

  if (typeof unique !== "string" || unique.length === 0) {
    throw new UserIdError("OutOfRange", `Unique string cannot be empty.`);
  }

  if (unique.length > USER_MAX_UNIQUE_LENGTH) {
    throw new UserIdError(
      "OutOfRange",
      `Unique string ${unique} exceeds maximum length. Expected at most ${String(USER_MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!USER_UNIQUE_PATTERN.test(unique)) {
    throw new UserIdError(
      "Format",
      `Unique string ${unique} contains invalid characters. Expected only [a-zA-Z0-9|_-].`,
    );
  }

  const sourceCode = USER_SOURCE_CODES[source];
  return `U${sourceCode}-${unique}` as UserId<S>;
};

/**
 * Canonical encoding is 'U' + source code + '-' + unique string.
 */
const userIdToString = (id: UserId): string => {
  return id as string;
};

/**
 * Parses a UserId from canonical encoding or throws a UserIdError.
 * (Accepts `/^U[23]-[a-zA-Z0-9|_-]{1,128}$/`.)
 */
const userIdFromString = (str: string): UserId => {
  if (typeof str !== "string" || str.length < 4) {
    throw new UserIdError("Format", `String ${str} is not a valid UserId. Expected format: U{source}-{unique}.`);
  }

  if (str.charAt(0) !== "U") {
    throw new UserIdError("Format", `String ${str} is not a valid UserId. Expected to start with 'U'.`);
  }

  const sourceCode = str.charAt(1);
  if (!(sourceCode in REVERSE_USER_SOURCE_CODES)) {
    throw new UserIdError(
      "InvalidSource",
      `String ${str} has invalid source code '${sourceCode}'. Expected one of: ${Object.keys(REVERSE_USER_SOURCE_CODES).join(", ")}.`,
    );
  }

  if (str.charAt(2) !== "-") {
    throw new UserIdError("Format", `String ${str} is not a valid UserId. Expected '-' separator at position 2.`);
  }

  const unique = str.slice(3);

  if (unique.length === 0) {
    throw new UserIdError("OutOfRange", `String ${str} has empty unique part.`);
  }

  if (unique.length > USER_MAX_UNIQUE_LENGTH) {
    throw new UserIdError(
      "OutOfRange",
      `String ${str} has unique part that exceeds maximum length. Expected at most ${String(USER_MAX_UNIQUE_LENGTH)} characters, got ${String(unique.length)}.`,
    );
  }

  if (!USER_UNIQUE_PATTERN.test(unique)) {
    throw new UserIdError(
      "Format",
      `String ${str} has unique part with invalid characters. Expected only [a-zA-Z0-9_-].`,
    );
  }

  return str as UserId;
};

/**
 * Extracts the component parts from a UserId.
 *
 * @param id - The UserId to decompose
 * @returns An object with source and unique properties
 */
const userIdToParts = <S extends UserSource = UserSource>(id: UserId<S>): { source: S; unique: string } => {
  const str = id as string;
  const sourceCode = str.charAt(1);
  const source = REVERSE_USER_SOURCE_CODES[sourceCode];
  const unique = str.slice(3); // Skip 'U', source code, and '-'

  if (!source) {
    throw new UserIdError("InvalidSource", `UserId ${str} has invalid source code '${sourceCode}'.`);
  }

  return { source: source as S, unique };
};

/**
 * Extracts the source from a UserId.
 */
const userIdSource = <S extends UserSource = UserSource>(id: UserId<S>): S => {
  return userIdToParts(id).source;
};

/**
 * Extracts the unique part from a UserId.
 */
const userIdUnique = (id: UserId): string => {
  return userIdToParts(id).unique;
};

export const UserId = {
  fromParts: userIdFromParts,
  fromString: userIdFromString,
  toString: userIdToString,
  toParts: userIdToParts,
  source: userIdSource,
  unique: userIdUnique,
} as const;

export const UserIdSchema = z.codec(z.string(), z.custom<UserId>(), {
  decode: (encoded) => UserId.fromString(encoded),
  encode: (decoded) => UserId.toString(decoded),
});

// #endregion

// #region Preview Users

export type UserScope = { kind: "customer" | "branch" | "test"; key: string };

const previewScopes: readonly UserScope[] = [{ kind: "test", key: "evaluation" }];

const users = new Map<string, readonly UserScope[]>([
  ["49cecb70-631d-447a-acc4-84f5959e4120", previewScopes], // Kristian Tasevski - ktasevski@boundinteractive.com
  ["f459162d-fe1b-4aac-ad7d-90492ab8f6b2", previewScopes], // Khalil Dahbeche - khalil.dahbeche@visioni.com.au
  ["fffd414a-2fdb-4f00-b8a6-b355bbe4708c", previewScopes], // Robin Mysell - robin.mysell@atfservices.com.au
  ["e4a37366-e44d-410e-8cdb-bfb4c08e4fcc", previewScopes], // Joe Van Wyk - joe.vanwyk@visioni.com.au
  ["33544942-1b52-4aa9-9643-eca1803e622a", previewScopes], // Michael Liebmann - michael.liebmann@atfservices.com.au
  ["bf3e09ca-228a-480f-88ea-48e294b916b1", previewScopes], // Michael Liebmann - michael.liebmann@visioni.com.au
  ["86b4d73f-4295-4ff5-8513-b6102e867359", previewScopes], // Grahame Hamilton - grahameh@bigpond.net.au
  ["b3189804-aebc-4efd-9f37-e6ba3113e35a", previewScopes], // Luke Schultz - luke.schultz@visioni.com.au
  ["8c77bcbf-daee-4586-989e-c13a85bdce00", previewScopes], // Alister Leong - alister.leong@visioni.com.au
  ["360de466-a86c-4a26-9ec9-417738ddb373", previewScopes], // Jono Schuster - jono.schuster@visioni.com.au
  ["e9317ccf-ff4e-46f8-907f-8d2c9fc09a21", previewScopes], // Beatrice Rueda - beatrice.m.rueda@gmail.com
  ["d3959843-12e3-4e57-8a9a-76efc2ec33f5", previewScopes], // Luuk Honings - luuk.honings@visioni.com.au
  ["6384bf43-11b0-4597-8463-a6abfefe100b", previewScopes], // Nick Silvestro - nick.silvestro@visioni.com.au
  ["7d3bf6c1-c697-44af-a8ac-ef08074b19d8", previewScopes], // Andres Puricelli - andres.puricelli@atfservices.com.au
  ["a96f475e-20da-4391-bb12-e751465b1184", previewScopes], // Margarette Mendoza - margarette.mendoza@visioni.com.au
  ["c94f3f38-c6fe-4251-8134-108a1e7c97bc", previewScopes], // Jon Maloney - jon.maloney@visioni.com.au
  ["b7b8886b-69b6-453f-a7cf-da2fdc624f3f", previewScopes], // Edmund Pye - edmund.pye@atfservices.com.au
  ["88799d79-5f47-4fd2-9148-13e0ae0898eb", previewScopes], // Nick Darvey - nick.darvey@visioni.com.au
  ["c39cec43-8f12-4b23-904c-7aa7182d3d9d", previewScopes], // A3S Test - devops+visionapp+a3s+test@visioni.com.au
  ["9a8809a9-315c-4630-8171-251f601f78c7", previewScopes], // Josh King - josh.king@visioni.com.au
  ["138a2d16-85df-40c6-a501-bb00a093b658", previewScopes], // Jack Svendsen - jack.svendsen@visioni.com.au
  ["1b91c79e-f950-411a-a963-b7e7ad8553f0", previewScopes], // Nick Doolan - nick.doolan@visioni.com.au
  ["c420373e-6812-403d-b451-981b1ecdb991", previewScopes], // Kyle Davies - kyle.davies@visioni.com.au
  ["49786a7a-b2bb-4695-ad86-2c6e644ca9c4", previewScopes], // Keanu Graham - keanu.graham@visioni.com.au
  ["4e25a40a-14bf-44c1-b468-8ba2051eee18", previewScopes], // Cameron Bell - cameron.bell@visioni.com.au
  ["47d9f411-b23c-4587-b526-eb15ccba0ee7", previewScopes], // John Sarmiento - john.sarmiento@visioni.com.au
  ["9ea25774-386e-4341-8526-8af0b7131e12", previewScopes], // Keyu Chen - keyu.chen@visioni.com.au
  ["e4c5cdb5-201b-4e5f-8213-2d6459878497", previewScopes], // Nha Luu - nha.luu@visioni.com.au
  ["7e734d16-b4a9-4806-929b-36959cab55e1", previewScopes], // David Capurso - david.capurso@visioni.com.au
  ["539283ee-4199-46a7-81ef-df69206846dd", previewScopes], // Andrew Cook - andrew.cook@visioni.com.au
  ["7859090e-65d8-4218-a1d4-8a70a0a08461", previewScopes], // Andrew Cook - andrew.cook@atfservices.com.au
  ["61509b4c-dfbf-4296-9aee-e75b5881d07f", previewScopes], // Matthew McGrath - matthew.mcgrath@visioni.com.au
  ["f0fb183d-de71-440c-abde-53a0247f5250", previewScopes], // Arnie Van Biljon - arnie.vanbiljon@visioni.com.au
  ["f663bc70-9f4a-4e36-9d47-92521cddc0c5", previewScopes], // Alan Cook - alan.cook@visioni.com.au
  ["52b2377f-9ec2-4f93-b7cb-97887984b686", previewScopes], // Rob Wakeham - rob.wakeham@visioni.com.au
  ["3b01f717-351e-48c6-b874-a789517f5022", previewScopes], // Shane Trenowden - shane.trenowden@visioni.com.au
  ["5e572eb5-2c32-48da-b22d-2f0473678de8", previewScopes], // Jeremy Cameron - jeremy.cameron@visioni.co.nz
  ["2f09ce37-3dba-495b-bab8-a0ded94ba476", previewScopes], // Adam Stanworth - adam.stanworth@visioni.com.au
  ["5c15cc96-4cc6-4c48-886d-c319d35eeb89", previewScopes], // Corney Swanepoel - corney.swanepoel@visioni.co.nz
  ["d1cd074c-81ec-433a-b66d-169213ce7b40", previewScopes], // Alex Mantaut - alex.mantaut@visioni.com.au
  ["9b22c9c9-e151-421d-a6a5-4c015f7f16f2", previewScopes], // Mark Finger - mark.finger@visioni.com.au
  ["9e4fd553-55a5-4526-91ea-7bc6b5e8cbcd", previewScopes], // Mark Finger - mark.finger+django-test-user-2@visioni.com.au
  ["39186211-548c-41b1-95de-457ad18e0281", previewScopes], // Peter Talacko - p.f.talacko@outlook.com
  ["627aeba8-7d7e-4b47-a0f9-16918d26f554", previewScopes], // Glen Taylor - glen@atfservices.co.nz
  ["13b2d31d-c77b-43cc-9360-470ec0124601", previewScopes], // Robbie Baigent - robbie@atfservices.co.nz
]);

export const getPreviewUserScopes = (user: UserId) =>
  UserId.source(user) === "legacy_app" ? (users.get(UserId.unique(user)) ?? []) : [];

// #endregion
